import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Plus, Edit2, Trash2, Check, X, Heart, MapPin, Loader2, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIES_OPTIONS = [
  { value: "canine", label: "Dogs", icon: "🐕" },
  { value: "feline", label: "Cats", icon: "🐱" },
  { value: "equine", label: "Horses", icon: "🐴" },
  { value: "cattle", label: "Cattle", icon: "🐄" },
  { value: "exotics", label: "Exotics", icon: "🦜" },
  { value: "others", label: "Farm Animals", icon: "🐖" },
];

const ShelterDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [shelter, setShelter] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewListing, setShowNewListing] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    species: "canine",
    breed: "",
    animal_name: "",
    age: "",
    sex: "",
    color: "",
    price: 0,
    photos: [],
    adoption_reason: "",
    good_with_kids: false,
    good_with_dogs: false,
    good_with_cats: false,
    house_trained: false,
    special_needs: "",
    rehoming_fee_note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("auth_token");
      const [profileRes, listingsRes] = await Promise.all([
        axios.get(`${API}/api/shelter/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/shelter/listings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setShelter(profileRes.data.shelter);
      setListings(listingsRes.data.listings || []);
    } catch (err) {
      logger.error("Failed to fetch shelter data:", err);
      if (err.response?.status === 403 || err.response?.status === 404) {
        navigate("/shelter/register");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      species: "canine",
      breed: "",
      animal_name: "",
      age: "",
      sex: "",
      color: "",
      price: 0,
      photos: [],
      adoption_reason: "",
      good_with_kids: false,
      good_with_dogs: false,
      good_with_cats: false,
      house_trained: false,
      special_needs: "",
      rehoming_fee_note: "",
    });
    setEditingListing(null);
    setShowNewListing(false);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setSubmitting(true);
    try {
      const token = sessionStorage.getItem("auth_token");
      
      if (editingListing) {
        await axios.put(`${API}/api/shelter/listings/${editingListing.listing_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Listing updated");
      } else {
        await axios.post(`${API}/api/shelter/listings`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Listing created");
      }
      
      resetForm();
      fetchData();
    } catch (err) {
      logger.error("Failed to save listing:", err);
      toast.error(err.response?.data?.detail || "Failed to save listing");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (listingId) => {
    if (!confirm("Are you sure you want to delete this listing?")) return;
    
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.delete(`${API}/api/shelter/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Listing deleted");
      fetchData();
    } catch (err) {
      logger.error("Failed to delete listing:", err);
      toast.error("Failed to delete listing");
    }
  };

  const handleMarkAdopted = async (listingId) => {
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.post(`${API}/api/shelter/listings/${listingId}/mark-adopted`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Marked as adopted!");
      fetchData();
    } catch (err) {
      logger.error("Failed to mark as adopted:", err);
      toast.error("Failed to mark as adopted");
    }
  };

  const startEdit = (listing) => {
    setFormData({
      title: listing.title || "",
      description: listing.description || "",
      species: listing.species || "canine",
      breed: listing.breed || "",
      animal_name: listing.animal_name || "",
      age: listing.age || "",
      sex: listing.sex || "",
      color: listing.color || "",
      price: listing.price || 0,
      photos: listing.photos || [],
      adoption_reason: listing.adoption_reason || "",
      good_with_kids: listing.good_with_kids || false,
      good_with_dogs: listing.good_with_dogs || false,
      good_with_cats: listing.good_with_cats || false,
      house_trained: listing.house_trained || false,
      special_needs: listing.special_needs || "",
      rehoming_fee_note: listing.rehoming_fee_note || "",
    });
    setEditingListing(listing);
    setShowNewListing(true);
  };

  if (loading) {
    return (
      <div className="pt-24 pb-16 min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F472B6] animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-[#F472B6]/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#F472B6]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl text-[#EDEDED]">{shelter?.organization_name}</h1>
                <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                  <MapPin className="w-3 h-3" />
                  {shelter?.location_city}, {shelter?.location_state}
                  {shelter?.is_verified && (
                    <span className="ml-2 px-2 py-0.5 bg-[#22C55E]/20 text-[#22C55E] text-xs rounded-full">
                      Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowNewListing(true)}
            className="px-4 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors flex items-center gap-2"
            data-testid="new-listing-btn"
          >
            <Plus className="w-4 h-4" />
            Add Animal
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4">
            <p className="text-2xl text-[#EDEDED]">{listings.filter(l => l.status === "active").length}</p>
            <p className="text-sm text-[#A1A1AA]">Active Listings</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-2xl text-[#F472B6]">{shelter?.total_adoptions || 0}</p>
            <p className="text-sm text-[#A1A1AA]">Total Adoptions</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-2xl text-[#EDEDED]">{shelter?.species?.length || 0}</p>
            <p className="text-sm text-[#A1A1AA]">Species Handled</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-2xl text-[#EDEDED]">{shelter?.verification_status === "approved" ? "✓" : "Pending"}</p>
            <p className="text-sm text-[#A1A1AA]">Verification</p>
          </div>
        </div>

        {/* New/Edit Listing Form */}
        {showNewListing && (
          <div className="glass-card p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl text-[#EDEDED]">
                {editingListing ? "Edit Listing" : "Add New Animal"}
              </h2>
              <button onClick={resetForm} className="text-[#71717A] hover:text-[#EDEDED]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Sweet Senior Dog Looking for Home"
                  className="input-minimal"
                  data-testid="listing-title"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Animal Name</label>
                <input
                  type="text"
                  value={formData.animal_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, animal_name: e.target.value }))}
                  placeholder="e.g., Buddy"
                  className="input-minimal"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Species</label>
                <select
                  value={formData.species}
                  onChange={(e) => setFormData(prev => ({ ...prev, species: e.target.value }))}
                  className="input-minimal"
                >
                  {SPECIES_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Breed</label>
                <input
                  type="text"
                  value={formData.breed}
                  onChange={(e) => setFormData(prev => ({ ...prev, breed: e.target.value }))}
                  placeholder="e.g., Labrador Mix"
                  className="input-minimal"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Age</label>
                <input
                  type="text"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  placeholder="e.g., 3 years"
                  className="input-minimal"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Sex</label>
                <select
                  value={formData.sex}
                  onChange={(e) => setFormData(prev => ({ ...prev, sex: e.target.value }))}
                  className="input-minimal"
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="neutered">Neutered Male</option>
                  <option value="spayed">Spayed Female</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Color</label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="e.g., Black and White"
                  className="input-minimal"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Rehoming Fee ($)</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                  className="input-minimal"
                  min="0"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Tell potential adopters about this animal..."
                  rows={3}
                  className="input-minimal"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">Adoption Reason</label>
                <input
                  type="text"
                  value={formData.adoption_reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, adoption_reason: e.target.value }))}
                  placeholder="e.g., Owner surrender, stray, rescued from hoarding situation"
                  className="input-minimal"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-3">Compatibility</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: "good_with_kids", label: "Good with Kids" },
                    { key: "good_with_dogs", label: "Good with Dogs" },
                    { key: "good_with_cats", label: "Good with Cats" },
                    { key: "house_trained", label: "House Trained" },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                      className={`px-3 py-1.5 rounded-lg border transition-colors ${
                        formData[opt.key]
                          ? "border-[#F472B6] bg-[#F472B6]/10 text-[#F472B6]"
                          : "border-[#3F3F46] text-[#A1A1AA] hover:border-[#71717A]"
                      }`}
                    >
                      {formData[opt.key] && <Check className="w-4 h-4 inline mr-1" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">Special Needs (if any)</label>
                <input
                  type="text"
                  value={formData.special_needs}
                  onChange={(e) => setFormData(prev => ({ ...prev, special_needs: e.target.value }))}
                  placeholder="e.g., Requires daily medication, needs experienced owner"
                  className="input-minimal"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="save-listing-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editingListing ? "Update" : "Create"} Listing
              </button>
            </div>
          </div>
        )}

        {/* Listings */}
        <div>
          <h2 className="text-xl text-[#EDEDED] mb-4">Your Listings</h2>
          
          {listings.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Heart className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <h3 className="text-lg text-[#EDEDED] mb-2">No Listings Yet</h3>
              <p className="text-[#A1A1AA] mb-4">Add your first animal to start helping them find homes.</p>
              <button
                onClick={() => setShowNewListing(true)}
                className="px-4 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors"
              >
                Add First Animal
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map(listing => (
                <div key={listing.listing_id} className="glass-card overflow-hidden">
                  <div className="aspect-video bg-[#18181B] relative">
                    {listing.photos?.[0] ? (
                      <img 
                        src={listing.photos[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">
                        {SPECIES_OPTIONS.find(s => s.value === listing.species)?.icon || "🐾"}
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        listing.status === "active" 
                          ? "bg-[#22C55E] text-black" 
                          : "bg-[#71717A] text-white"
                      }`}>
                        {listing.status === "sold" ? "Adopted" : listing.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-[#EDEDED] font-medium mb-1 truncate">{listing.title}</h3>
                    <p className="text-sm text-[#A1A1AA] mb-2">
                      {listing.animal_name && `${listing.animal_name} • `}
                      {listing.breed || "Unknown breed"}
                    </p>
                    <p className="text-[#F472B6] text-sm mb-3">
                      {listing.price > 0 ? `$${listing.price} rehoming fee` : "Free to good home"}
                    </p>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/listing/${listing.listing_id}`}
                        className="flex-1 px-3 py-1.5 text-center text-sm border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
                      >
                        <ExternalLink className="w-3 h-3 inline mr-1" />
                        View
                      </Link>
                      <button
                        onClick={() => startEdit(listing)}
                        className="p-1.5 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {listing.status === "active" && (
                        <button
                          onClick={() => handleMarkAdopted(listing.listing_id)}
                          className="p-1.5 border border-[#22C55E] text-[#22C55E] rounded-lg hover:bg-[#22C55E]/10 transition-colors"
                          title="Mark as Adopted"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(listing.listing_id)}
                        className="p-1.5 border border-[#EF4444] text-[#EF4444] rounded-lg hover:bg-[#EF4444]/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShelterDashboard;
